/**
 * Level meter ballistics.
 *
 * The frame header carries a pre-encode linear peak per channel (PROTOCOL.md §3.1),
 * so meters need no decoded audio at all. Raw header peaks flicker at 50 Hz though,
 * so the displayed value snaps up instantly and falls back over a fixed time
 * constant — the behaviour every hardware meter has, and the reason a transient
 * stays visible long enough to read.
 */
export class PeakMeter {
  private level = 0;
  private hold = 0;
  private holdUntil = 0;

  constructor(
    /** Seconds for the level to fall by a factor of e. */
    private readonly releaseSeconds = 0.35,
    /** How long the peak-hold tick stays put. */
    private readonly holdSeconds = 1.2,
  ) {}

  push(peak: number, atMs: number): void {
    const value = Number.isFinite(peak) ? Math.max(0, Math.min(1, peak)) : 0;
    if (value >= this.level) this.level = value;
    if (value >= this.hold) {
      this.hold = value;
      this.holdUntil = atMs + this.holdSeconds * 1000;
    }
  }

  /** Advance the decay to `atMs` and read the current value. */
  sample(atMs: number, elapsedMs: number): { level: number; hold: number } {
    const decay = Math.exp(-Math.max(0, elapsedMs) / (this.releaseSeconds * 1000));
    this.level *= decay;
    if (this.level < 1e-4) this.level = 0;
    if (atMs > this.holdUntil) this.hold = Math.max(this.level, this.hold * decay);
    return { level: this.level, hold: this.hold };
  }

  reset(): void {
    this.level = 0;
    this.hold = 0;
    this.holdUntil = 0;
  }
}

/** Linear amplitude to dBFS, floored so a silent stream does not read -Infinity. */
export function toDecibels(linear: number, floorDb = -60): number {
  if (linear <= 0) return floorDb;
  return Math.max(floorDb, 20 * Math.log10(linear));
}

/** Map dBFS onto 0..1 for a meter bar, with the top of the scale at 0 dBFS. */
export function meterFraction(linear: number, floorDb = -60): number {
  const db = toDecibels(linear, floorDb);
  return Math.max(0, Math.min(1, (db - floorDb) / -floorDb));
}
